import {TranslationObject} from '@colonist/i18n'
import {CardEnum, CardStateUnordered, CurrentState, PlayerColor, TurnState} from '@colonist/game-states'
import {ArrayUtils} from '@colonist/utils'
import {BaseCardData, CardClickPopupType, CardDataController} from '@colonist/game-card-types'
import {ActionsThatCanBeCancelled} from '@colonist/game-base-types'
import {CurrentStateValidator} from '@colonist/game-mechanic-validators'

export const getCardsToGreyForBankTrade = (myBankTradeRatioState: {[key: string]: number}, cardState: CardStateUnordered): CardEnum[] => {
    const cardsToGrey: CardEnum[] = []
    for(const [cardEnum, requiredRatio] of Object.entries(myBankTradeRatioState)) {
        const cardCount = cardState[cardEnum] ?? 0

        if(cardCount < requiredRatio) {
            for(let i = 0; i < cardCount; i++) {
                cardsToGrey.push(Number(cardEnum) as CardEnum)
            }
        }
    }
    return cardsToGrey
}

const isValidTurnState = (cardData: BaseCardData, currentState: CurrentState): boolean => {
    if(cardData.allowableTurnStates == undefined) return true
    if(cardData.allowableTurnStates.includes(currentState.turnState)) return true

    return false
}

export const canClickCard = ({
    cardEnum,
    currentState,
    isReplay,
    myColor,
    translationKeyContext,
    isShowTradePanel,
    isActionBoxPickCardOpening,
    currentStateValidator,
}: {
    cardEnum: CardEnum
    currentState: CurrentState
    isReplay: boolean
    myColor: PlayerColor
    translationKeyContext: string
    isShowTradePanel: boolean
    isActionBoxPickCardOpening: boolean
    currentStateValidator: CurrentStateValidator
}): {
    canClickCard: boolean
    helperText: TranslationObject | undefined
} => {
    const cardData = CardDataController.getCardDataForCard(cardEnum)
    if(!cardData) {
        return {
            canClickCard: false,
            helperText: undefined,
        }
    }
    if(isReplay) {
        return {
            canClickCard: false,
            helperText: {key: 'strings:game.errors.replayMode'},
        }
    }
    if(isActionBoxPickCardOpening) {
        return {
            canClickCard: true,
            helperText: undefined,
        }
    }
    if(isShowTradePanel) {
        if(cardData.clickPopupType == CardClickPopupType.TradeWindow || cardData.cardEnum == CardEnum.AnyResource) {
            return {
                canClickCard: true,
                helperText: undefined,
            }
        }
        return {
            canClickCard: false,
            helperText: {
                key: 'strings:game.tooltips.cannotTradeDevCard',
                options: {
                    context: translationKeyContext,
                },
            },
        }
    }
    const isNotMyTurn = !currentStateValidator.currentTurnPlayers.includes(myColor)
    if(isNotMyTurn) {
        return {
            canClickCard: false,
            helperText: {key: 'strings:game.errors.notYourTurn'},
        }
    }
    const isInSpecialBuildPhase = currentState.turnState == TurnState.SpecialBuildPhase
    if(isInSpecialBuildPhase) {
        const doesClickingCardTriggerTrade = cardData.clickPopupType == CardClickPopupType.TradeWindow
        const devCardsDisabledErrorMessage: TranslationObject = {
            key: 'strings:game.errors.developmentCardsDisabled',
            options: {context: translationKeyContext},
        }
        const errorMessage: TranslationObject = doesClickingCardTriggerTrade ? {key: 'strings:game.errors.tradingDisabled'} : devCardsDisabledErrorMessage
        return {
            canClickCard: false,
            helperText: errorMessage,
        }
    }

    if(!ArrayUtils.elementInArray(ActionsThatCanBeCancelled.actions, currentStateValidator.getPlayerActionState(myColor))) {
        return {
            canClickCard: false,
            helperText: {key: 'strings:game.errors.finishCurrentAction'},
        }
    }

    if(!isValidTurnState(cardData, currentState)) {
        const isCardAchemist = cardEnum == CardEnum.ProgressCardScienceAlchemist
        const errorMessage: TranslationObject = isCardAchemist ? {key: 'strings:game.errors.canOnlyBePlayedBeforeRollingDice'} : {key: 'strings:game.errors.finishCurrentAction'}

        return {
            canClickCard: false,
            helperText: errorMessage,
        }
    }
    return {canClickCard: true, helperText: undefined}
}

export const getCanClickCardDataForAllCards = ({
    allMyCards,
    currentState,
    isReplay,
    myColor,
    translationKeyContext,
    isShowTradePanel,
    isActionBoxPickCardOpening,
    currentStateValidator,
}: {
    allMyCards: CardEnum[]
    currentState: CurrentState
    isReplay: boolean
    myColor: PlayerColor
    translationKeyContext: string
    isShowTradePanel: boolean
    isActionBoxPickCardOpening: boolean
    currentStateValidator: CurrentStateValidator
}) => {
    const cardsHelperTextLocal: {[key in CardEnum]?: {
        canClickCard: boolean
        helperText: TranslationObject | undefined
    }} = {}
    const myUniqueAllCards = ArrayUtils.getUniqueElements(allMyCards)

    for(const cardEnum of myUniqueAllCards) {
        const canClickCardData = canClickCard({
            cardEnum: cardEnum,
            currentState: currentState,
            isReplay: isReplay,
            myColor: myColor,
            translationKeyContext: translationKeyContext,
            isShowTradePanel: isShowTradePanel,
            isActionBoxPickCardOpening: isActionBoxPickCardOpening,
            currentStateValidator: currentStateValidator,
        })
        cardsHelperTextLocal[cardEnum] = canClickCardData
    }
    return cardsHelperTextLocal
}

export const removeCardsSelected = (resourceCards: CardEnum[], selectedResourceCards?: CardEnum[]): CardEnum[] => {
    if(selectedResourceCards != null) {
        const myCards = [...resourceCards]
        ArrayUtils.removeElementsFromArray(myCards, selectedResourceCards)
        return myCards
    }
    return resourceCards
}
